import React, { useState, useEffect } from "react";
import {
  Plus, Search, Trash2, ArrowLeft, X, CheckCircle, XCircle,
  DollarSign, Users, TrendingUp, Building, CreditCard, FileText,
  Filter, RefreshCw, AlertTriangle, ChevronDown, ChevronUp,
  Target, Wallet, BarChart2, CheckSquare, Square, Eye,
} from "lucide-react";
import { GeneralContributionService } from "../components/generalContributions/GeneralContributionService";
import { useNavigate } from "react-router-dom";

// ─── Design tokens — flat system matching Majanga/Cheleko ────────────────────
const NAV  = "#001f3f";
const TEAL = "#0d9488";

// ─── Reusable atoms ───────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    approved:  "bg-emerald-100 text-emerald-700 border border-emerald-200",
    rejected:  "bg-red-100 text-red-700 border border-red-200",
    pending:   "bg-amber-100 text-amber-700 border border-amber-200",
    cancelled: "bg-slate-100 text-slate-600 border border-slate-200",
    active:    "bg-teal-100 text-teal-700 border border-teal-200",
    inactive:  "bg-slate-100 text-slate-600 border border-slate-200",
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${map[status?.toLowerCase()] || map.cancelled}`}>
      {status}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, accent = NAV }) => (
  <div className="bg-white rounded-xl p-5 border border-slate-200 flex items-center gap-4">
    <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: accent + "18" }}>
      <Icon size={20} style={{ color: accent }} />
    </div>
    <div>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
      <p className="text-xl font-bold text-slate-800 leading-tight">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const Tab = ({ icon: Icon, label, active, onClick, badge }) => (
  <button onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${
      active ? "border-teal-500 text-teal-600" : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
    }`}>
    <Icon size={15} />
    <span>{label}</span>
    {badge > 0 && <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{badge}</span>}
  </button>
);

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl border border-slate-200 overflow-hidden ${className}`}>{children}</div>
);

const CardHeader = ({ title, subtitle, action }) => (
  <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
    <div>
      <h2 className="text-sm font-bold text-slate-800">{title}</h2>
      {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
    </div>
    {action}
  </div>
);

const Inp = ({ label, required, ...props }) => (
  <div>
    {label && <label className="block text-xs font-medium text-slate-600 mb-1.5">{label}{required && <span className="text-red-400 ml-0.5">*</span>}</label>}
    <input className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-300" {...props} />
  </div>
);

const Sel = ({ label, required, children, ...props }) => (
  <div>
    {label && <label className="block text-xs font-medium text-slate-600 mb-1.5">{label}{required && <span className="text-red-400 ml-0.5">*</span>}</label>}
    <select className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-300" {...props}>
      {children}
    </select>
  </div>
);

const Btn = ({ children, onClick, disabled, variant = "primary", size = "md" }) => {
  const sizes  = { sm: "px-3 py-1.5 text-xs", md: "px-4 py-2 text-sm", lg: "px-5 py-2.5 text-sm" };
  const styles = {
    primary: { background: `linear-gradient(135deg, ${NAV}, ${TEAL})`, color: "#fff" },
    teal:    { backgroundColor: TEAL, color: "#fff" },
    success: { backgroundColor: "#10b981", color: "#fff" },
    danger:  { backgroundColor: "#f43f5e", color: "#fff" },
    outline: {},
  };
  const cls = {
    primary: "hover:opacity-90",
    teal:    "hover:opacity-90",
    success: "hover:opacity-90",
    danger:  "hover:opacity-90",
    outline: "border border-slate-200 text-slate-700 hover:bg-slate-50",
  };
  return (
    <button onClick={onClick} disabled={disabled}
      className={`inline-flex items-center gap-2 font-semibold rounded-xl transition-all disabled:opacity-50 ${sizes[size]} ${cls[variant]}`}
      style={styles[variant]}>
      {children}
    </button>
  );
};

// ─── Progress bar ──────────────────────────────────────────────────────────────
const Progress = ({ value, max, className = "" }) => {
  const pct = max ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className={`w-full bg-slate-100 rounded-full h-1.5 ${className}`}>
      <div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: pct >= 100 ? "#10b981" : TEAL }} />
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
const GeneralContributions = () => {
  const navigate  = useNavigate();
  const [activeTab, setActiveTab] = useState("funds");
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [success, setSuccess]     = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [categories,    setCategories]    = useState([]);
  const [funds,         setFunds]         = useState([]);
  const [accounts,      setAccounts]      = useState([]);
  const [contributions, setContributions] = useState([]);
  const [summaries,     setSummaries]     = useState([]);

  const [newCategory, setNewCategory] = useState({ name:"", category_type:"majanga", description:"", icon:"🚨", is_active:true });
  const [newFund, setNewFund]         = useState({ name:"", category:"", description:"", target_amount:"", currency:"TZS", is_active:true, is_public:true, allow_contributions:true, require_approval:true });
  const [newAccount, setNewAccount]   = useState({ fund:"", account_name:"", bank_name:"", account_number:"", branch_name:"", minimum_amount:"", is_mobile_money:false, mobile_money_provider:"", is_primary:false, is_active:true });

  const [filterStatus, setFilterStatus]     = useState("all");
  const [selectedContributions, setSelectedContributions] = useState([]);

  // auto-clear messages
  useEffect(() => { if (success) { const t = setTimeout(() => setSuccess(null), 3000); return () => clearTimeout(t); } }, [success]);

  useEffect(() => { fetchCategories(); fetchFunds(); }, []);
  useEffect(() => {
    if (activeTab === "accounts")      fetchAccounts();
    if (activeTab === "contributions") fetchContributions();
    if (activeTab === "summaries")     fetchSummaries();
  }, [activeTab]);

  const fetchCategories = async () => { try { setCategories(await GeneralContributionService.getAllCategories()); } catch { setError("Failed to load categories"); } };
  const fetchFunds      = async () => { try { setFunds(await GeneralContributionService.getAllFunds()); } catch { setError("Failed to load funds"); } };
  const fetchAccounts   = async () => { try { setAccounts(await GeneralContributionService.getAllAccounts()); } catch { setError("Failed to load accounts"); } };
  const fetchContributions = async () => {
    setLoading(true);
    try {
      const filters = filterStatus !== "all" ? { status: filterStatus } : {};
      setContributions(await GeneralContributionService.getAllContributions(filters));
      setError(null);
    } catch { setError("Failed to load contributions"); }
    finally { setLoading(false); }
  };
  const fetchSummaries = async () => { try { setSummaries(await GeneralContributionService.getAllSummaries()); } catch { setError("Failed to load summaries"); } };

  const handleCreateCategory = async () => {
    if (!newCategory.name || !newCategory.description) { setError("Fill all required fields"); return; }
    setLoading(true);
    try {
      await GeneralContributionService.createCategory(newCategory);
      await fetchCategories();
      setNewCategory({ name:"", category_type:"majanga", description:"", icon:"🚨", is_active:true });
      setSuccess("Category created!");
      setError(null);
    } catch { setError("Failed to create category"); }
    finally { setLoading(false); }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try { await GeneralContributionService.deleteCategory(id); await fetchCategories(); } catch { setError("Failed to delete category"); }
  };

  const handleCreateFund = async () => {
    if (!newFund.name || !newFund.category || !newFund.description) { setError("Fill all required fields"); return; }
    setLoading(true);
    try {
      await GeneralContributionService.createFund(newFund);
      await fetchFunds();
      setNewFund({ name:"", category:"", description:"", target_amount:"", currency:"TZS", is_active:true, is_public:true, allow_contributions:true, require_approval:true });
      setSuccess("Fund created!");
      setActiveTab("funds");
      setError(null);
    } catch { setError("Failed to create fund"); }
    finally { setLoading(false); }
  };

  const handleDeleteFund = async (id) => {
    if (!window.confirm("Delete this fund?")) return;
    try { await GeneralContributionService.deleteFund(id); await fetchFunds(); } catch { setError("Failed to delete fund"); }
  };

  const handleCreateAccount = async () => {
    if (!newAccount.fund || !newAccount.account_name || !newAccount.bank_name || !newAccount.account_number || !newAccount.minimum_amount) { setError("Fill all required fields"); return; }
    setLoading(true);
    try {
      await GeneralContributionService.createAccount(newAccount);
      await fetchAccounts();
      setNewAccount({ fund:"", account_name:"", bank_name:"", account_number:"", branch_name:"", minimum_amount:"", is_mobile_money:false, mobile_money_provider:"", is_primary:false, is_active:true });
      setSuccess("Account added!");
      setActiveTab("accounts");
      setError(null);
    } catch { setError("Failed to create account"); }
    finally { setLoading(false); }
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm("Delete this account?")) return;
    try { await GeneralContributionService.deleteAccount(id); await fetchAccounts(); } catch { setError("Failed to delete account"); }
  };

  const handleApproveContribution = async (id) => {
    try { await GeneralContributionService.approveContribution(id); await fetchContributions(); setSuccess("Contribution approved!"); } catch { setError("Failed to approve"); }
  };

  const handleRejectContribution = async (id) => {
    const reason = prompt("Rejection reason:");
    if (!reason) return;
    try { await GeneralContributionService.rejectContribution(id, reason); await fetchContributions(); } catch { setError("Failed to reject"); }
  };

  const handleBulkApprove = async () => {
    if (!selectedContributions.length) { setError("Select contributions first"); return; }
    if (!window.confirm(`Approve ${selectedContributions.length} contributions?`)) return;
    setLoading(true);
    try {
      await GeneralContributionService.bulkApproveContributions(selectedContributions);
      await fetchContributions();
      setSelectedContributions([]);
      setSuccess(`${selectedContributions.length} contributions approved!`);
    } catch { setError("Bulk approve failed"); }
    finally { setLoading(false); }
  };

  // ── Computed ──────────────────────────────────────────────────────────────
  const filteredFunds = funds.filter(f =>
    f.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const pendingContribs = contributions.filter(c => c.status === "pending").length;
  const totalBalance    = funds.reduce((s, f) => s + parseFloat(f.current_balance || 0), 0);
  const totalContribs   = contributions.length;

  const tabs = [
    { id:"funds",         label:"Funds (Mfuko)",   icon:Wallet },
    { id:"categories",    label:"Categories",       icon:FileText },
    { id:"accounts",      label:"Accounts",         icon:Building },
    { id:"contributions", label:"Contributions",    icon:CreditCard, badge:pendingContribs },
    { id:"summaries",     label:"Summaries",        icon:TrendingUp },
    { id:"addFund",       label:"Add Fund",          icon:Plus },
    { id:"addAccount",    label:"Add Account",       icon:Plus },
  ];

  // ── Checkbox helpers ───────────────────────────────────────────────────────
  const allPendingIds = contributions.filter(c => c.status === "pending").map(c => c.id);
  const allChecked    = allPendingIds.length > 0 && allPendingIds.every(id => selectedContributions.includes(id));

  return (
    <div className="flex flex-col min-h-screen bg-slate-50">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-100 px-6 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/majanga")} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 transition-colors">
            <ArrowLeft size={16} />
          </button>
          <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `linear-gradient(135deg, ${NAV}, ${TEAL})` }}>
            <Wallet size={16} className="text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-800 leading-none">General Contributions</p>
            <p className="text-[10px] text-slate-400">Funds, accounts & contribution management</p>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1"><Wallet size={12} style={{ color:TEAL }}/>{funds.length} funds</span>
          <span className="flex items-center gap-1" style={{ color:TEAL }}>TZS {totalBalance.toLocaleString()} total</span>
          {pendingContribs > 0 && <span className="flex items-center gap-1 text-amber-600 font-semibold"><AlertTriangle size={12}/>{pendingContribs} pending</span>}
        </div>
      </div>

      {/* ── Tab bar ────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-100 px-4 flex-shrink-0 overflow-x-auto">
        <div className="flex">
          {tabs.map(t => (
            <Tab key={t.id} icon={t.icon} label={t.label} active={activeTab === t.id} onClick={() => setActiveTab(t.id)} badge={t.badge} />
          ))}
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mx-6 mt-3 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm flex-shrink-0">
          <AlertTriangle size={15} className="flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)}><X size={13}/></button>
        </div>
      )}
      {success && (
        <div className="mx-6 mt-3 flex items-center gap-3 bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-xl text-sm flex-shrink-0">
          <CheckCircle size={15} className="flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center gap-3" style={{ color:TEAL }}>
            <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor:`${TEAL} transparent transparent transparent` }}/>
            <span className="text-sm font-medium">Loading...</span>
          </div>
        </div>
      )}

      {/* ── Content ────────────────────────────────────────────────────── */}
      <main className="flex-1 overflow-y-auto p-6 space-y-6">

        {/* ══ FUNDS ══════════════════════════════════════════════════════ */}
        {!loading && activeTab === "funds" && (
          <div className="space-y-4">
            {/* Quick stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Wallet}     label="Total Funds"         value={funds.length}                    accent={NAV} />
              <StatCard icon={TrendingUp} label="Total Balance"       value={`TZS ${totalBalance.toLocaleString()}`} accent={TEAL} />
              <StatCard icon={Users}      label="Contributions"       value={totalContribs}                    accent="#3b82f6" />
              <StatCard icon={AlertTriangle} label="Pending Approval" value={pendingContribs}                  accent="#f59e0b" />
            </div>

            {/* Search + add */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-xs">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input type="text" placeholder="Search funds..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-teal-300"/>
              </div>
              <Btn onClick={() => setActiveTab("addFund")}><Plus size={14}/> New Fund</Btn>
            </div>

            {/* Fund cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredFunds.map(fund => (
                <div key={fund.id} className="bg-white rounded-xl border border-slate-200 p-5 hover:border-slate-300 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-slate-800 truncate">{fund.name}</h3>
                      <p className="text-xs text-slate-400 mt-0.5">{fund.category_name}</p>
                    </div>
                    <button onClick={() => handleDeleteFund(fund.id)} className="p-1.5 hover:bg-red-50 text-red-400 rounded-lg ml-2 flex-shrink-0 transition-colors">
                      <Trash2 size={14}/>
                    </button>
                  </div>

                  <p className="text-xs text-slate-500 mb-4 line-clamp-2">{fund.description}</p>

                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">Balance</span>
                      <span className="font-bold" style={{ color:TEAL }}>TZS {parseFloat(fund.current_balance||0).toLocaleString()}</span>
                    </div>
                    {fund.target_amount && (
                      <>
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-500">Target</span>
                          <span className="font-medium text-slate-700">TZS {parseFloat(fund.target_amount).toLocaleString()}</span>
                        </div>
                        <Progress value={parseFloat(fund.current_balance||0)} max={parseFloat(fund.target_amount)} />
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-400">Progress</span>
                          <span className="font-semibold text-slate-600">{fund.progress_percentage?.toFixed(1)}%</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">Contributors</span>
                      <span className="font-medium text-slate-700">{fund.total_contributors||0}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                    <StatusBadge status={fund.is_active?"active":"inactive"}/>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${fund.is_public?"bg-blue-50 text-blue-700 border border-blue-200":"bg-orange-50 text-orange-700 border border-orange-200"}`}>
                      {fund.is_public?"Public":"Private"}
                    </span>
                    {fund.require_approval && <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">Approval req.</span>}
                  </div>
                </div>
              ))}
            </div>

            {filteredFunds.length === 0 && (
              <Card>
                <div className="py-16 text-center text-slate-400">
                  <Wallet size={32} className="mx-auto mb-3 opacity-20"/>
                  <p className="text-sm">No funds found</p>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* ══ ADD FUND ═══════════════════════════════════════════════════ */}
        {!loading && activeTab === "addFund" && (
          <div className="max-w-2xl">
            <button onClick={() => setActiveTab("funds")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-5"><ArrowLeft size={15}/> Back</button>
            <Card>
              <CardHeader title="Create New Fund" subtitle="Set up a new contribution fund"/>
              <div className="p-6 grid grid-cols-2 gap-4">
                <Inp label="Fund Name" required value={newFund.name} onChange={e => setNewFund({...newFund,name:e.target.value})} placeholder="e.g., Emergency Relief Fund 2025"/>
                <Sel label="Category" required value={newFund.category} onChange={e => setNewFund({...newFund,category:e.target.value})}>
                  <option value="">Select Category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                </Sel>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Description <span className="text-red-400">*</span></label>
                  <textarea value={newFund.description} onChange={e => setNewFund({...newFund,description:e.target.value})} rows={3} placeholder="Describe the purpose of this fund"
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 resize-none"/>
                </div>
                <Inp label="Target Amount (optional)" type="number" value={newFund.target_amount} onChange={e => setNewFund({...newFund,target_amount:e.target.value})} placeholder="5000000"/>
                <Sel label="Currency" value={newFund.currency} onChange={e => setNewFund({...newFund,currency:e.target.value})}>
                  <option value="TZS">TZS</option><option value="USD">USD</option><option value="EUR">EUR</option>
                </Sel>
                <div className="col-span-2 grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { key:"is_active",          label:"Active" },
                    { key:"is_public",           label:"Public" },
                    { key:"allow_contributions", label:"Allow Contributions" },
                    { key:"require_approval",    label:"Require Approval" },
                  ].map(({ key, label }) => (
                    <label key={key} className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-all ${newFund[key]?"border-teal-300 bg-teal-50":"border-slate-200 hover:border-slate-300"}`}>
                      <input type="checkbox" checked={newFund[key]} onChange={e => setNewFund({...newFund,[key]:e.target.checked})} className="sr-only"/>
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${newFund[key]?"border-teal-500 bg-teal-500":"border-slate-300"}`}>
                        {newFund[key] && <CheckCircle size={10} className="text-white"/>}
                      </div>
                      <span className="text-xs font-medium text-slate-700">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-3 px-6 pb-6 pt-2 border-t border-slate-100">
                <Btn variant="outline" onClick={() => setActiveTab("funds")}>Cancel</Btn>
                <Btn onClick={handleCreateFund} disabled={loading}>Create Fund</Btn>
              </div>
            </Card>
          </div>
        )}

        {/* ══ ADD ACCOUNT ════════════════════════════════════════════════ */}
        {!loading && activeTab === "addAccount" && (
          <div className="max-w-2xl">
            <button onClick={() => setActiveTab("accounts")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-5"><ArrowLeft size={15}/> Back</button>
            <Card>
              <CardHeader title="Add Account to Fund" subtitle="Link a bank or mobile money account"/>
              <div className="p-6 grid grid-cols-2 gap-4">
                <Sel label="Select Fund" required value={newAccount.fund} onChange={e => setNewAccount({...newAccount,fund:e.target.value})}>
                  <option value="">Select Fund</option>
                  {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </Sel>
                <Inp label="Account Name" required value={newAccount.account_name} onChange={e => setNewAccount({...newAccount,account_name:e.target.value})} placeholder="e.g., Emergency Fund – CRDB"/>
                <Inp label="Bank Name" required value={newAccount.bank_name} onChange={e => setNewAccount({...newAccount,bank_name:e.target.value})} placeholder="e.g., CRDB Bank"/>
                <Inp label="Account Number" required value={newAccount.account_number} onChange={e => setNewAccount({...newAccount,account_number:e.target.value})} placeholder="0150123456789"/>
                <Inp label="Branch Name" value={newAccount.branch_name} onChange={e => setNewAccount({...newAccount,branch_name:e.target.value})} placeholder="e.g., Kariakoo Branch"/>
                <Inp label="Minimum Amount" required type="number" value={newAccount.minimum_amount} onChange={e => setNewAccount({...newAccount,minimum_amount:e.target.value})} placeholder="10000"/>
                <div className="col-span-2 flex items-center gap-4 flex-wrap">
                  <label className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer ${newAccount.is_mobile_money?"border-teal-300 bg-teal-50":"border-slate-200"}`}>
                    <input type="checkbox" checked={newAccount.is_mobile_money} onChange={e => setNewAccount({...newAccount,is_mobile_money:e.target.checked})} className="sr-only"/>
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${newAccount.is_mobile_money?"border-teal-500 bg-teal-500":"border-slate-300"}`}>
                      {newAccount.is_mobile_money && <CheckCircle size={10} className="text-white"/>}
                    </div>
                    <span className="text-xs font-medium text-slate-700">Mobile Money</span>
                  </label>
                  {newAccount.is_mobile_money && (
                    <Sel value={newAccount.mobile_money_provider} onChange={e => setNewAccount({...newAccount,mobile_money_provider:e.target.value})}>
                      <option value="">Select Provider</option>
                      <option value="mpesa">M-Pesa</option>
                      <option value="tigopesa">Tigo Pesa</option>
                      <option value="airtelmoney">Airtel Money</option>
                      <option value="halopesa">Halo Pesa</option>
                    </Sel>
                  )}
                  <label className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer ${newAccount.is_primary?"border-teal-300 bg-teal-50":"border-slate-200"}`}>
                    <input type="checkbox" checked={newAccount.is_primary} onChange={e => setNewAccount({...newAccount,is_primary:e.target.checked})} className="sr-only"/>
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${newAccount.is_primary?"border-teal-500 bg-teal-500":"border-slate-300"}`}>
                      {newAccount.is_primary && <CheckCircle size={10} className="text-white"/>}
                    </div>
                    <span className="text-xs font-medium text-slate-700">Primary Account</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-3 px-6 pb-6 pt-2 border-t border-slate-100">
                <Btn variant="outline" onClick={() => setActiveTab("accounts")}>Cancel</Btn>
                <Btn onClick={handleCreateAccount} disabled={loading}>Add Account</Btn>
              </div>
            </Card>
          </div>
        )}

        {/* ══ ACCOUNTS ═══════════════════════════════════════════════════ */}
        {!loading && activeTab === "accounts" && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Btn onClick={() => setActiveTab("addAccount")}><Plus size={14}/> Add Account</Btn>
            </div>
            <Card>
              <CardHeader title={`Accounts (${accounts.length})`} subtitle="Bank and mobile money accounts linked to funds"/>
              <table className="min-w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    {["Fund","Account Name","Bank","Account No.","Min Amount","Type","Status","Actions"].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {accounts.map(acc => (
                    <tr key={acc.id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-5 py-3.5 text-sm text-slate-700 font-medium">{funds.find(f => f.id === acc.fund)?.name||"N/A"}</td>
                      <td className="px-5 py-3.5">
                        <div className="text-sm font-semibold text-slate-800">{acc.account_name}</div>
                        {acc.is_primary && <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full font-semibold">Primary</span>}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{acc.bank_name}</td>
                      <td className="px-5 py-3.5 text-sm font-mono text-slate-600">{acc.account_number}</td>
                      <td className="px-5 py-3.5 text-sm font-semibold" style={{ color:TEAL }}>TZS {parseFloat(acc.minimum_amount).toLocaleString()}</td>
                      <td className="px-5 py-3.5">
                        {acc.is_mobile_money
                          ? <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">Mobile</span>
                          : <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">Bank</span>
                        }
                      </td>
                      <td className="px-5 py-3.5"><StatusBadge status={acc.is_active?"active":"inactive"}/></td>
                      <td className="px-5 py-3.5">
                        <button onClick={() => handleDeleteAccount(acc.id)} className="p-1.5 hover:bg-red-50 text-red-400 rounded-lg transition-colors"><Trash2 size={14}/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {accounts.length === 0 && <div className="py-14 text-center text-slate-400"><Building size={28} className="mx-auto mb-2 opacity-30"/><p className="text-sm">No accounts found</p></div>}
            </Card>
          </div>
        )}

        {/* ══ CONTRIBUTIONS ══════════════════════════════════════════════ */}
        {!loading && activeTab === "contributions" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Filter size={14} className="text-slate-400"/>
                <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); fetchContributions(); }}
                  className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-300">
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              {selectedContributions.length > 0 && (
                <Btn variant="success" onClick={handleBulkApprove}>
                  <CheckCircle size={14}/> Approve Selected ({selectedContributions.length})
                </Btn>
              )}
              <span className="text-xs text-slate-400 ml-auto">{contributions.length} total · {pendingContribs} pending</span>
            </div>

            <Card>
              <table className="min-w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="px-5 py-3">
                      <button onClick={() => {
                        if (allChecked) setSelectedContributions([]);
                        else setSelectedContributions(allPendingIds);
                      }} className="text-slate-400 hover:text-slate-600">
                        {allChecked ? <CheckSquare size={15}/> : <Square size={15}/>}
                      </button>
                    </th>
                    {["User","Fund","Amount","Method","Date","Status","Actions"].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {contributions.map(c => (
                    <tr key={c.id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-5 py-3.5">
                        {c.status === "pending" && (
                          <button onClick={() => {
                            setSelectedContributions(prev =>
                              prev.includes(c.id) ? prev.filter(id => id !== c.id) : [...prev, c.id]
                            );
                          }} className="text-slate-400 hover:text-teal-500">
                            {selectedContributions.includes(c.id) ? <CheckSquare size={15} style={{ color:TEAL }}/> : <Square size={15}/>}
                          </button>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-700">{c.user_email}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{c.fund_name}</td>
                      <td className="px-5 py-3.5 text-sm font-bold" style={{ color:TEAL }}>TZS {parseFloat(c.amount).toLocaleString()}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-500 capitalize">{c.payment_method?.replace("_"," ")}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{c.payment_date ? new Date(c.payment_date).toLocaleDateString() : "—"}</td>
                      <td className="px-5 py-3.5"><StatusBadge status={c.status}/></td>
                      <td className="px-5 py-3.5">
                        {c.status === "pending" && (
                          <div className="flex gap-1">
                            <button onClick={() => handleApproveContribution(c.id)} className="p-1.5 hover:bg-green-50 text-green-500 rounded-lg transition-colors" title="Approve"><CheckCircle size={14}/></button>
                            <button onClick={() => handleRejectContribution(c.id)} className="p-1.5 hover:bg-red-50 text-red-400 rounded-lg transition-colors" title="Reject"><XCircle size={14}/></button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {contributions.length === 0 && <div className="py-14 text-center text-slate-400"><CreditCard size={28} className="mx-auto mb-2 opacity-30"/><p className="text-sm">No contributions found</p></div>}
            </Card>
          </div>
        )}

        {/* ══ CATEGORIES ═════════════════════════════════════════════════ */}
        {!loading && activeTab === "categories" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Create form */}
            <Card>
              <CardHeader title="Create Category" subtitle="Add a new contribution category"/>
              <div className="p-5 space-y-3">
                <Inp label="Name" required value={newCategory.name} onChange={e => setNewCategory({...newCategory,name:e.target.value})} placeholder="e.g., Health Emergency"/>
                <Sel label="Type" value={newCategory.category_type} onChange={e => setNewCategory({...newCategory,category_type:e.target.value})}>
                  {[["majanga","Majanga (Emergency)"],["chereko","Chereko (Celebration)"],["investment","Investment"],["planning","Planning"],["savings","Savings"],["welfare","Welfare"],["education","Education"],["health","Health"],["other","Other"]].map(([v,l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </Sel>
                <Inp label="Icon (emoji)" value={newCategory.icon} onChange={e => setNewCategory({...newCategory,icon:e.target.value})} placeholder="🚨"/>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Description <span className="text-red-400">*</span></label>
                  <textarea value={newCategory.description} onChange={e => setNewCategory({...newCategory,description:e.target.value})} rows={3}
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 resize-none"/>
                </div>
                <Btn onClick={handleCreateCategory} disabled={loading} className="w-full justify-center">
                  <Plus size={14}/> Create Category
                </Btn>
              </div>
            </Card>

            {/* List */}
            <Card>
              <CardHeader title={`Categories (${categories.length})`}/>
              <div className="divide-y divide-slate-50">
                {categories.map(cat => (
                  <div key={cat.id} className="px-5 py-3.5 flex items-center gap-3 hover:bg-slate-50 transition-colors">
                    <span className="text-2xl w-10 text-center flex-shrink-0">{cat.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800">{cat.name}</p>
                      <p className="text-xs text-slate-400 truncate">{cat.description}</p>
                      <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded-full font-medium capitalize">{cat.category_type}</span>
                    </div>
                    <button onClick={() => handleDeleteCategory(cat.id)} className="p-1.5 hover:bg-red-50 text-red-400 rounded-lg transition-colors flex-shrink-0">
                      <Trash2 size={14}/>
                    </button>
                  </div>
                ))}
                {categories.length === 0 && <div className="py-10 text-center text-slate-400 text-sm">No categories yet</div>}
              </div>
            </Card>
          </div>
        )}

        {/* ══ SUMMARIES ══════════════════════════════════════════════════ */}
        {!loading && activeTab === "summaries" && (
          <div className="space-y-4">
            {/* Per-fund totals */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {funds.filter(f => f.current_balance > 0).map(fund => (
                <div key={fund.id} className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-sm font-bold text-slate-800">{fund.name}</p>
                      <p className="text-xs text-slate-400">{fund.category_name}</p>
                    </div>
                    <span className="text-lg">{categories.find(c => c.id === fund.category)?.icon || "💰"}</span>
                  </div>
                  <p className="text-xl font-bold" style={{ color:TEAL }}>TZS {parseFloat(fund.current_balance||0).toLocaleString()}</p>
                  {fund.target_amount && (
                    <div className="mt-2">
                      <Progress value={parseFloat(fund.current_balance||0)} max={parseFloat(fund.target_amount)} className="mt-1"/>
                      <p className="text-xs text-slate-400 mt-1">{fund.progress_percentage?.toFixed(1)}% of TZS {parseFloat(fund.target_amount).toLocaleString()} target</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <Card>
              <CardHeader title="Contributor Summaries"/>
              <table className="min-w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    {["User","Fund","Total Contributed","Count","Last Contribution"].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {summaries.map(s => (
                    <tr key={s.id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-5 py-3.5 text-sm text-slate-700">{s.user_email}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{s.fund_name}</td>
                      <td className="px-5 py-3.5 text-sm font-bold" style={{ color:TEAL }}>TZS {parseFloat(s.total_contributed).toLocaleString()}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{s.contribution_count}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{s.last_contribution_date ? new Date(s.last_contribution_date).toLocaleDateString():"—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {summaries.length === 0 && <div className="py-14 text-center text-slate-400"><BarChart2 size={28} className="mx-auto mb-2 opacity-30"/><p className="text-sm">No summaries yet</p></div>}
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default GeneralContributions;